import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import Link from "@mui/material/Link";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import axios from "axios";

import MDBox from "components/MDBox";
import StatusBadge from "components/ReportUI/Statusbadge";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";

import { trackingReportEndpoint } from "config";
import TrackingReportTable from "./trackingreporttable";




function TrackingReport() {
    const [reportData, setReportData] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [vehicleId, setVehicleId] = useState("");
    const [loading, setLoading] = useState(false);
    const [date, setDate] = useState(new Date());
    const [interval, setInterval] = useState("None");


    const { fetchThings, things } = useContext(AccountsContext);

    useEffect(() => {
        fetchThings();
    }, []);

    // Snackbar
    const openSuccessSB = () => setSuccessSB(true);
    const closeSuccessSB = () => setSuccessSB(false);
    const openErrorSB = () => setErrorSB(true);
    const closeErrorSB = () => setErrorSB(false);

    // Columns for Tracking Report
    const columns = [
        { Header: "Date", accessor: "lastTrackedTime", width: "20%" },
        { Header: "Vehicle Number", accessor: "thingId", width: "20%" },
        { Header: "Speed", accessor: "speed", align: "center" },
        {
          Header: "Ignition Status",
          accessor: "ignitionStatus",
          align: "center",
          Cell: ({ value }) => (
            <StatusBadge value={value} trueLabel="ON" falseLabel="OFF" trueValues={["on", "true", "1", "running"]} />
          ),
        },
        {
            Header: "Location",
            accessor: "locationLink",
            Cell: (cell) => {
                const value = cell.value;  // safer
                return value ? (
                    <a href={value} target="_blank" rel="noopener noreferrer">
                        📍
                    </a>
                ) : (
                    "-"
                );
            },
        }



    ];

    // Format date to API format
    const formatDate = (d) => {
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
    };

    const fetchReport = async () => {
        if (!vehicleId) {
            setErrorMessage("Please select a vehicle");
            openErrorSB();
            return;
        }

        const token = localStorage.getItem("token");
        setLoading(true);
        setErrorMessage("");

        try {
            const data = {
                date: formatDate(date),
                zone: "Asia/Kolkata",
                startTime: `${formatDate(date)} 00:00`,
                endTime: `${formatDate(date)} 23:59`,
                things: vehicleId,
                thingIds: [vehicleId],
                geofence: [],
                groups: [],
                interval: interval === "None" ? null : interval, 
            };

            const config = {
                method: "post",
                url: trackingReportEndpoint,
                headers: {
                    "x-dhundhoo-session": token,
                    "Content-Type": "application/json",
                },
                data,
            };

            const response = await axios.request(config);
            setReportData(response.data);
            openSuccessSB();
        } catch (error) {
            console.error(error);
            setErrorMessage("Error loading tracking report");
            openErrorSB();
        } finally {
            setLoading(false);
        }
    };

    const clearFilters = () => {
        setVehicleId("");
        setReportData([]);
        setErrorMessage("");
        setDate(new Date());
    };
    const exportToCSV = () => {
        if (reportData.length === 0) {
            setErrorMessage("No data to export");
            openErrorSB();
            return;
        }

        const headers = ["Date", "Vehicle Number", "Speed", "Ignition Status", "Location Link"];

        const rows = reportData.map((row) => [
            row.lastTrackedTime,
            row.thingId,
            row.speed,
            row.ignitionStatus,
            row.locationLink
        ].join(","));

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `TrackingReport.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox p={3} pb={0}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <Grid container spacing={2}>
                                        {/* Vehicle Select */}
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Autocomplete
                                                options={things || []}
                                                getOptionLabel={(option) => option.thingId}
                                                value={things.find((t) => t.thingId === vehicleId) || null}
                                                onChange={(event, newValue) => {
                                                    setVehicleId(newValue ? newValue.thingId : "");
                                                }}
                                                renderOption={(props, option) => (
                                                    <li
                                                        {...props}
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            width: "100%",
                                                        }}
                                                    >
                                                        <span>{option.nickName}</span>
                                                        <span style={{ color: "#666" }}>{option.thingId}</span>
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField {...params} placeholder="Select Vehicle" fullWidth size="small" />
                                                )}
                                            />
                                        </Grid>

                                        {/* Date Picker */}
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DatePicker
                                                label="Select Date"
                                                value={date}
                                                onChange={(newValue) => setDate(newValue)}
                                                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                                            />
                                        </Grid>
                                       
                                        <Grid item xs={6} sm={3} md={2}>
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                options={["None", 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60]}
                                                value={interval}
                                                onChange={(e, newValue) => setInterval(newValue)}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Interval (sec)" variant="outlined" />
                                                )}
                                            />
                                        </Grid>


                                        {/* Buttons */}
                                        <Grid item xs={4} sm={2} md={1}>
                                            <MDButton
                                                fullWidth
                                                variant="gradient"
                                                color="info"
                                                size="small"
                                                onClick={fetchReport}
                                                disabled={loading}
                                            >
                                                {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                                            </MDButton>
                                        </Grid>
                                        <Grid item xs={4} sm={2} md={1}>
                                            <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading} />
                                        </Grid>

                                        <Grid item xs={4} sm={2} md={1}>
                                            <MDButton
                                                fullWidth
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={clearFilters}
                                                disabled={loading}
                                            >
                                                CLEAR
                                            </MDButton>
                                        </Grid>
                                    </Grid>
                                </LocalizationProvider>
                            </MDBox>

                            {/* Error Alert */}
                            {errorMessage && (
                                <MDBox px={2} pt={2}>
                                    <Alert severity="error">{errorMessage}</Alert>
                                </MDBox>
                            )}

                            {/* Loader or Table */}
                            <MDBox pt={3} display="flex" justifyContent="center">
                                {loading ? (
                                    <CircularProgress />
                                ) : (
                                    <TrackingReportTable
                                        table={{ columns, rows: reportData }}
                                        isSorted={true}
                                        entriesPerPage={false}
                                        showTotalEntries={true}
                                        noEndBorder
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* Snackbars */}
            <MDSnackbar
                color="success"
                icon="check"
                title="Tracking Report"
                content="Data Loaded Successfully"
                open={successSB}
                onClose={closeSuccessSB}
                close={closeSuccessSB}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Tracking Report"
                content="Failed to load report"
                open={errorSB}
                onClose={closeErrorSB}
                close={closeErrorSB}
                bgWhite
            />
        </DashboardLayout>
    );
}

export default TrackingReport;




